import React from "react"
import { Link as RouterLink } from "react-router-dom"
import { Container, Typography, Box, Paper, Link } from "@mui/material"

const SupportPage = () => {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f9f9f9", py: 4 }}>
      <Container maxWidth="md">
        <Paper elevation={2} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 2 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              color: "#8E2DE2",
              borderBottom: "2px solid #eee",
              pb: 1,
              mb: 3,
            }}
          >
            Support
          </Typography>

          <Typography paragraph>Welcome to TNKL support! We're here to help you find and share the best bathroom locations.</Typography>

          {/* Contact Box */}
          <Paper
            sx={{
              bgcolor: "#f8f8f8",
              borderLeft: "4px solid #8E2DE2",
              p: 2.5,
              my: 3,
            }}
            elevation={0}
          >
            <Typography variant="h6" gutterBottom>
              Contact Us
            </Typography>
            <Typography paragraph>
              <strong>Email</strong>:{" "}
              <Link href="mailto:support@crappermapper.com" sx={{ color: "#8E2DE2" }}>
                support@crappermapper.com
              </Link>
            </Typography>
            <Typography paragraph>
              <strong>Website</strong>:{" "}
              <Link href="https://tnkl.app" sx={{ color: "#8E2DE2" }}>
                https://tnkl.app
              </Link>
            </Typography>
            {/* <Typography>We typically respond within 24-48 hours.</Typography> */}
          </Paper>

          {/* FAQ Section */}
          <Typography variant="h4" component="h2" sx={{ color: "#4a4a4a", mt: 4, mb: 2 }}>
            Frequently Asked Questions
          </Typography>

          <FAQItem
            question="How do I add a new bathroom location?"
            answer='Tap the "+" button on the map or list screen. Fill in the details including name, location or address, accessibility features, and amenities. You can also add photos to help other users!'
          />

          <FAQItem
            question="Why does the app need location permissions?"
            answer="Location permissions allow us to show you nearby bathrooms, provide location-based notifications, and help you accurately add new bathroom locations. You can disable location services in your device settings, but some features will be limited."
          />

          <FAQItem
            question="How do I report inappropriate content?"
            answer="Tap the flag icon on any bathroom listing, review, or photo to report it. Our moderation team will review flagged content within 24 hours."
          />

          <FAQItem
            question="How do points and achievements work?"
            answer="You earn points by contributing to the community: adding bathrooms, writing reviews, uploading photos, and helping other users. Points unlock achievements and improve your leaderboard ranking. Points are for fun and have no monetary value."
          />

          {/* <FAQItem
            question="Can I edit or delete a bathroom I added?"
            answer="Yes! Go to the bathroom details page and tap the edit icon. You can update information or delete the listing if it's no longer accurate."
          /> */}

          <FAQItem
            question="How do I delete my account?"
            answer={<>Go to Profile Tab → Settings → Account → Delete Account. We'll delete your account and associated data within 30 days.</>}
          />

          <FAQItem
            question="Why can't I see some bathrooms on the map?"
            answer="Bathrooms may be filtered based on your current zoom level or search criteria. Try zooming in or adjusting your filters. Also, make sure you have a stable internet connection."
          />

          <FAQItem
            question="How do I turn off notifications?"
            answer="Go to Settings → Notifications and adjust your preferences. You can customize notification distance, types, and completely disable them if you prefer."
          />

          <FAQItem
            question="Is my data private and secure?"
            answer={
              <>
                Yes! We take your privacy seriously. Please read our{" "}
                <Link component={RouterLink} to="/privacy" sx={{ color: "#8E2DE2" }}>
                  Privacy Policy
                </Link>{" "}
                to understand how we collect, use, and protect your data.
              </>
            }
          />

          {/* Technical Issues */}
          <Typography variant="h4" component="h2" sx={{ color: "#4a4a4a", mt: 4, mb: 2 }}>
            Technical Issues
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#4a4a4a", mb: 1 }}>
              The app is crashing or not loading
            </Typography>
            <Typography paragraph>Try these steps:</Typography>
            <Box component="ol" sx={{ pl: 2.5 }}>
              <li>Force close the app and restart it</li>
              <li>Check your internet connection</li>
              <li>Update to the latest version of the app</li>
              <li>Restart your device</li>
              <li>
                If the problem persists, contact us at{" "}
                <Link href="mailto:support@crappermapper.com" sx={{ color: "#8E2DE2" }}>
                  support@crappermapper.com
                </Link>
              </li>
            </Box>
          </Box>

          <FAQItem
            question="Photos won't upload"
            answer="Make sure you've granted the app permission to access your photos in your device settings. Also check that you have a stable internet connection and the photo file isn't too large (max 10MB)."
          />

          {/* Community Guidelines */}
          <Typography variant="h4" component="h2" sx={{ color: "#4a4a4a", mt: 4, mb: 2 }}>
            Community Guidelines
          </Typography>
          <Typography paragraph>
            Please be respectful and helpful when using TNKL. Review our{" "}
            <Link component={RouterLink} to="/terms" sx={{ color: "#8E2DE2" }}>
              Terms of Service
            </Link>{" "}
            for detailed community guidelines.
          </Typography>

          {/* Feature Requests */}
          <Typography variant="h4" component="h2" sx={{ color: "#4a4a4a", mt: 4, mb: 2 }}>
            Feature Requests
          </Typography>
          <Typography paragraph>
            Have an idea to make TNKL better? We'd love to hear it! Email us at{" "}
            <Link href="mailto:support@crappermapper.com" sx={{ color: "#8E2DE2" }}>
              support@crappermapper.com
            </Link>{" "}
            with your suggestions.
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#4a4a4a", mb: 1 }}>
        {question}
      </Typography>
      <Typography>{answer}</Typography>
    </Box>
  )
}

export default SupportPage
